import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ISurveyBuilderSurvey } from "../../../../../../../@types/SurveyBuilder";
import { Button } from "../../../../../../../components/Button";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../../components/TextField";
import {
  KbildDomains,
  LpfSymptomsDomains,
  ScoreStrategiesWithDomains,
  ScoreStrategiesWithFixedDomains,
} from "../../../../../../../constants/surveys";
import { surveyBuilderService } from "../../../../../../../services/surveyBuilderService";
import {
  Chip,
  Chips,
  ErrorText,
  FieldRow,
  DomainEditor,
  DomainInputRow,
  IconButton,
  Panel,
  PanelActions,
  PanelHeader,
  PanelTitle,
  PillButton,
  TitleGroup,
  ValueText,
} from "../../../../SurveyBuilder.styles";
import {
  HARDCODED_SURVEY_CODES,
  isValidSurveyCode,
} from "../../../../surveyCode";
import {
  EditorCancelButton,
  EditorFooter,
} from "../QuestionEditor/QuestionEditor.styles";

interface ISurveyDetailsCardProps {
  survey: ISurveyBuilderSurvey;
  languageCode: string;
  readOnly: boolean;
  surveyTypeLabel?: string;
  scoreStrategyOptions: IOption[];
  isSaving: boolean;
  run(
    action: () => Promise<ISurveyBuilderSurvey>,
    successMessage?: string
  ): Promise<ISurveyBuilderSurvey | undefined>;
}

// "" is the "No scoring" option; the API wants no strategy at all.
const NO_SCORING = "";

/** EN-5261: the version's title, description and scoring, plus the survey's code. */
export function SurveyDetailsCard({
  survey,
  languageCode,
  readOnly,
  surveyTypeLabel,
  scoreStrategyOptions,
  isSaving,
  run,
}: ISurveyDetailsCardProps) {
  const { t } = useTranslation();
  const { version } = survey;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scoreStrategy, setScoreStrategy] = useState(NO_SCORING);
  const [domains, setDomains] = useState<string[]>([]);
  const [domainToAdd, setDomainToAdd] = useState("");
  const [code, setCode] = useState("");
  const [editing, setEditing] = useState(false);

  const resetToSaved = () => {
    setName(version.name ?? "");
    setDescription(version.description ?? "");
    setScoreStrategy(version.scoreStrategy ?? NO_SCORING);
    setDomains(version.domains);
    setDomainToAdd("");
    setCode(survey.code ?? "");
  };

  // Another version or language: leave edit mode and show what is saved there.
  useEffect(() => {
    resetToSaved();
    setEditing(false);
  }, [version.id, languageCode]);

  const headerChanged =
    name !== (version.name ?? "") ||
    description !== (version.description ?? "") ||
    scoreStrategy !== (version.scoreStrategy ?? NO_SCORING) ||
    domains.join("\n") !== version.domains.join("\n");
  const codeChanged = code !== (survey.code ?? "");
  const hasDomains = ScoreStrategiesWithDomains.includes(scoreStrategy);
  const fixedDomains = ScoreStrategiesWithFixedDomains.includes(scoreStrategy);

  const handleStrategyChange = (value: string) => {
    setScoreStrategy(value);
    if (value === "KBILD") setDomains(KbildDomains);
    else if (value === "LpfSymptomsScore") setDomains(LpfSymptomsDomains);
    else if (!ScoreStrategiesWithDomains.includes(value)) setDomains([]);
  };

  const handleAddDomain = () => {
    const domain = domainToAdd.trim();
    if (domain === "" || domains.includes(domain)) return;
    setDomains([...domains, domain]);
    setDomainToAdd("");
  };

  const handleSave = async () => {
    const message = t("surveys_builder_toasts_detailsSaved");

    if (codeChanged) {
      const saved = await run(
        () =>
          surveyBuilderService.updateCode(
            survey.id,
            code || undefined,
            languageCode
          ),
        headerChanged ? undefined : message
      );
      if (!saved) return;
    }

    if (headerChanged) {
      const saved = await run(
        () =>
          surveyBuilderService.updateHeader(version.id, {
            languageCode,
            name: name.trim(),
            description: description.trim() || undefined,
            scoreStrategy: scoreStrategy || undefined,
            domains: hasDomains ? domains : [],
            rowVersion: version.rowVersion,
          }),
        message
      );
      if (!saved) return;
    }

    setEditing(false);
  };

  const handleCancel = () => {
    resetToSaved();
    setEditing(false);
  };

  const savedStrategy = version.scoreStrategy ?? NO_SCORING;
  const scoringLabel =
    scoreStrategyOptions.find((o) => o.value === savedStrategy)?.label ??
    t("surveys_builder_content_noScoring");

  return (
    <Panel>
      <PanelHeader>
        <TitleGroup>
          <PanelTitle>{t("surveys_builder_details_title")}</PanelTitle>
        </TitleGroup>
        {!readOnly && !editing && (
          <PanelActions>
            <IconButton
              type="button"
              disabled={isSaving}
              onClick={() => setEditing(true)}
            >
              {t("surveys_builder_content_edit")}
            </IconButton>
          </PanelActions>
        )}
      </PanelHeader>

      {editing ? (
        <>
          {/* Fixed once the survey exists: shown like Scoring, but not editable. */}
          <FieldRow as="div">
            {t("surveys_builder_content_type")}
            <SelectDropdown
              options={[
                {
                  value: survey.type ?? "",
                  label: surveyTypeLabel ?? survey.type ?? "",
                  key: "type",
                },
              ]}
              value={survey.type ?? ""}
              disabled
              width={460}
            />
          </FieldRow>
          <FieldRow>
            {t("surveys_builder_surveyInfo_surveyTitle")}
            <TextField
              label=""
              maxLength={200}
              value={name}
              disabled={readOnly}
              backgroudColor="white"
              onChange={(e) => setName(e.target.value)}
            />
          </FieldRow>
          <FieldRow>
            {t("surveys_builder_surveyInfo_surveyDescription")}
            <TextField
              label=""
              value={description}
              disabled={readOnly}
              backgroudColor="white"
              placeholder={t("surveys_builder_descriptionPlaceholder")}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FieldRow>
          <FieldRow as="div">
            {t("surveys_builder_content_scoring")}
            <SelectDropdown
              options={[
                {
                  value: NO_SCORING,
                  label: t("surveys_builder_content_noScoring"),
                  key: "no-scoring",
                },
                ...scoreStrategyOptions,
              ]}
              value={scoreStrategy}
              disabled={readOnly}
              width={460}
              onValueChange={(value) => handleStrategyChange(value as string)}
            />
          </FieldRow>

          {hasDomains && (
            <FieldRow as="div">
              {t("surveys_builder_content_domains")}
              <DomainEditor>
                {domains.length > 0 && (
                  <Chips>
                    {domains.map((domain) => (
                      <Chip key={domain}>
                        {domain}
                        {!readOnly && !fixedDomains && (
                          <button
                            type="button"
                            aria-label={t(
                              "surveys_builder_content_removeDomain",
                              {
                                domain,
                              }
                            )}
                            onClick={() =>
                              setDomains(domains.filter((d) => d !== domain))
                            }
                          >
                            ×
                          </button>
                        )}
                      </Chip>
                    ))}
                  </Chips>
                )}
                {!readOnly && !fixedDomains && (
                  <DomainInputRow>
                    <TextField
                      label=""
                      value={domainToAdd}
                      backgroudColor="white"
                      placeholder={t(
                        "surveys_builder_content_domainPlaceholder"
                      )}
                      errorMessage={
                        domains.includes(domainToAdd.trim())
                          ? t("surveys_builder_content_domainExists")
                          : undefined
                      }
                      onChange={(e) => setDomainToAdd(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
                    />
                    <PillButton
                      type="button"
                      tone="light"
                      disabled={domainToAdd.trim() === ""}
                      onClick={handleAddDomain}
                    >
                      {t("surveys_builder_content_addDomain")}
                    </PillButton>
                  </DomainInputRow>
                )}
              </DomainEditor>
            </FieldRow>
          )}

          <FieldRow>
            {t("surveys_builder_surveyInfo_surveyCode")}
            <div>
              <TextField
                label=""
                value={code}
                disabled={readOnly}
                backgroudColor="white"
                onChange={(e) => setCode(e.target.value)}
              />
              {!isValidSurveyCode(code) && (
                <ErrorText>
                  {t("surveys_builder_surveyInfo_codeFormat")}
                </ErrorText>
              )}
            </div>
          </FieldRow>
          {codeChanged &&
            HARDCODED_SURVEY_CODES.includes(survey.code ?? "") && (
              <ErrorText>
                <Trans
                  i18nKey="surveys_builder_surveyCodeWarning"
                  values={{ codes: HARDCODED_SURVEY_CODES.join(", ") }}
                  components={{ bold: <strong /> }}
                />
              </ErrorText>
            )}
          <EditorFooter>
            <Button
              type="button"
              label={t("surveys_builder_question_save")}
              disabled={
                isSaving ||
                (!headerChanged && !codeChanged) ||
                name.trim() === "" ||
                !isValidSurveyCode(code)
              }
              isLoading={isSaving}
              onClick={handleSave}
            />
            <EditorCancelButton onClick={handleCancel}>
              {t("common_cancel")}
            </EditorCancelButton>
          </EditorFooter>
        </>
      ) : (
        <>
          <FieldRow as="div">
            {t("surveys_builder_content_type")}
            <ValueText>{surveyTypeLabel ?? survey.type}</ValueText>
          </FieldRow>
          <FieldRow as="div">
            {t("surveys_builder_surveyInfo_surveyTitle")}
            <ValueText>{version.name}</ValueText>
          </FieldRow>
          <FieldRow as="div">
            {t("surveys_builder_surveyInfo_surveyDescription")}
            <ValueText muted={!version.description}>
              {version.description || "—"}
            </ValueText>
          </FieldRow>
          <FieldRow as="div">
            {t("surveys_builder_content_scoring")}
            <ValueText>{scoringLabel}</ValueText>
          </FieldRow>
          {version.domains.length > 0 && (
            <FieldRow as="div">
              {t("surveys_builder_content_domains")}
              <Chips>
                {version.domains.map((domain) => (
                  <Chip key={domain}>{domain}</Chip>
                ))}
              </Chips>
            </FieldRow>
          )}
          <FieldRow as="div">
            {t("surveys_builder_surveyInfo_surveyCode")}
            <ValueText muted={!survey.code}>{survey.code || "—"}</ValueText>
          </FieldRow>
        </>
      )}
    </Panel>
  );
}
